import React, { useCallback, useEffect, useMemo, useState } from "react";
import withShipment from "../../withShipment";
import { apiRequest } from "../../redux/actions/api";
import { userInfoSelector } from "../../redux/selectors/auth";
import {
  LOCATION,
  LIZ_ACCOUNTING,
} from "../global/Server/endpoints";
import { currentWeekInfo, toISO } from "./utils";
import Landing from "./Landing";
import { MonthsScreen, WeeksScreen, WeeklyDetail, ATMDetail } from "./Detail";
import { SettingsMain, LocsList, LocDetail, PartnersList } from "./Settings";
import Spinner from "./Spinner";
import "./ProfitTracker.css";

// Screen map:
//   landing  -> Landing (3 tabs inside)
//   months   -> MonthsScreen
//   weeks    -> WeeksScreen
//   detail   -> WeeklyDetail | ATMDetail (depending on ctx.mode)
//   settings -> SettingsMain
//   sLocs    -> LocsList
//   sPartners -> PartnersList

// Edit operations across the Profit Tracker require ROLE_ADMIN (or
// ROLE_MASTER_ADMIN). Without those, every screen renders in view-only
// mode: no Refresh/Save/Edit buttons, no "+ Add" affordances. Matches
// the authority-checking pattern used by the Loans module.
function deriveIsAdmin(userInfo) {
  const info = userInfo || {};
  const set = new Set();
  if (info.authority) set.add(info.authority);
  const list = info.authorities || [];
  list.forEach((a) => {
    const value = typeof a === "string" ? a : a?.authority;
    if (value) set.add(value);
  });
  return set.has("ROLE_ADMIN");
}

function ProfitTrackerBase({ apiRequest: dispatchRequest, userInfo }) {
  const isAdmin = useMemo(() => deriveIsAdmin(userInfo), [userInfo]);
  const now = new Date();
  const currentWeek = useMemo(() => currentWeekInfo(now) || {
    year: now.getFullYear(),
    month: now.getMonth() + 1,
    week: null,
  }, []); // eslint-disable-line

  const [screen, setScreen] = useState("landing");
  const [ctx, setCtx] = useState({
    year: currentWeek.year,
    month: currentWeek.month,
    week: currentWeek.week,
    mode: "weekly",
  });
  const [locations, setLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(true);
  const [locationsError, setLocationsError] = useState(null);

  const nav = useCallback((next, patch) => {
    if (patch) setCtx((p) => ({ ...p, ...patch }));
    setScreen(next);
    // scroll to top on screen change
    const scroller = document.querySelector(".pt-scroll");
    if (scroller) scroller.scrollTop = 0;
  }, []);

  // load locations once on mount
  useEffect(() => {
    setLocationsLoading(true);
    setLocationsError(null);
    dispatchRequest({
      key: "profitTracker/locations",
      endpoint: LOCATION.LIST,
      method: "GET",
      silent: true,
      onSuccess: (res) => {
        const list = Array.isArray(res)
          ? res
          : Array.isArray(res?.locations)
          ? res.locations
          : [];
        setLocations(list);
        setLocationsLoading(false);
      },
      onError: (err) => {
        setLocationsError(typeof err === "string" ? err : "Failed to load locations");
        setLocationsLoading(false);
      },
    });
  }, [dispatchRequest]);

  // Shared API hitters passed down to children.
  const api = useMemo(() => ({
    saveLocation: ({ name }, onSuccess, onError) => {
      dispatchRequest({
        key: "profitTracker/saveLocation",
        endpoint: LOCATION.SAVE_LOCATION,
        method: "POST",
        data: { name },
        silent: true,
        onSuccess,
        onError,
      });
    },
    renameLocation: ({ locationId, name }, onSuccess, onError) => {
      dispatchRequest({
        key: "profitTracker/renameLocation",
        endpoint: LOCATION.RENAME_LOCATION,
        method: "POST",
        data: { locationId, name },
        silent: true,
        onSuccess,
        onError,
      });
    },
    deleteLocation: ({ locationId }, onSuccess, onError) => {
      dispatchRequest({
        key: "profitTracker/deleteLocation",
        endpoint: LOCATION.DELETE_LOCATION,
        method: "POST",
        data: { locationId },
        silent: true,
        onSuccess,
        onError,
      });
    },
    saveInvestor: (payload, onSuccess, onError) => {
      dispatchRequest({
        key: "profitTracker/saveInvestor",
        endpoint: LOCATION.SAVE_INVESTOR,
        method: "POST",
        data: payload,
        silent: true,
        onSuccess,
        onError,
      });
    },
    updateInvestor: (payload, onSuccess, onError) => {
      dispatchRequest({
        key: "profitTracker/updateInvestor",
        endpoint: LOCATION.UPDATE_INVESTOR,
        method: "POST",
        data: payload,
        silent: true,
        onSuccess,
        onError,
      });
    },
    deleteInvestor: ({ investorId }, onSuccess, onError) => {
      dispatchRequest({
        key: "profitTracker/deleteInvestor",
        endpoint: LOCATION.DELETE_INVESTOR,
        method: "POST",
        data: { investorId },
        silent: true,
        onSuccess,
        onError,
      });
    },
    fetchLocationSummary: ({ fromDate, toDate, locationNames }, onSuccess, onError) => {
      dispatchRequest({
        key: "profitTracker/locationSummary",
        endpoint: LIZ_ACCOUNTING.LOCATION_SUMMARY,
        method: "POST",
        data: {
          fromDate: toISO(fromDate),
          toDate: toISO(toDate),
          locations: locationNames,
        },
        silent: true,
        onSuccess,
        onError,
      });
    },
    fetchWeek: ({ year, month, weekNumber }, onSuccess, onError) => {
      dispatchRequest({
        key: "profitTracker/getWeek",
        endpoint: LIZ_ACCOUNTING.GET_WEEK,
        method: "POST",
        data: { year, month, weekNumber },
        silent: true,
        onSuccess,
        onError,
      });
    },
    saveWeek: (payload, onSuccess, onError) => {
      dispatchRequest({
        key: "profitTracker/saveWeek",
        endpoint: LIZ_ACCOUNTING.SAVE_WEEK,
        method: "POST",
        data: payload,
        silent: true,
        onSuccess,
        onError,
      });
    },
    fetchATMWeek: ({ year, month, weekNumber }, onSuccess, onError) => {
      dispatchRequest({
        key: "profitTracker/getATMWeek",
        endpoint: LIZ_ACCOUNTING.GET_ATM_WEEK_DATA,
        method: "POST",
        data: { year, month, weekNumber },
        silent: true,
        onSuccess,
        onError,
      });
    },
    saveATMWeek: (payload, onSuccess, onError) => {
      dispatchRequest({
        key: "profitTracker/saveATMWeek",
        endpoint: LIZ_ACCOUNTING.SAVE_ATM_WEEK_DATA,
        method: "POST",
        data: payload,
        silent: true,
        onSuccess,
        onError,
      });
    },
    fetchAnnual: ({ year, locationNames }, onSuccess, onError) => {
      dispatchRequest({
        key: "profitTracker/annual",
        endpoint: LIZ_ACCOUNTING.LOCATION_YEAR_PROFIT_SUMMARY,
        method: "POST",
        data: { year, locations: locationNames },
        silent: true,
        onSuccess,
        onError,
      });
    },
  }), [dispatchRequest]);

  const locationNames = useMemo(() => locations.map((l) => l.name), [locations]);

  if (locationsLoading) {
    return (
      <div className="pt-root">
        <div className="pt-frame">
          <Spinner label="Loading Profit Tracker…" minHeight="60vh" />
        </div>
      </div>
    );
  }

  return (
    <div className="pt-root">
      <div className="pt-frame">
        {locationsError && (
          <div style={{ padding: '10px 16px 0' }}>
            <div className="pt-error">{locationsError}</div>
          </div>
        )}
        <div className="pt-scroll">
          {screen === 'landing' && (
            <Landing
              nav={nav}
              api={api}
              currentWeek={currentWeek}
              locationNames={locationNames}
              isAdmin={isAdmin}
            />
          )}
          {screen === 'months' && (
            <MonthsScreen nav={nav} ctx={ctx} currentWeek={currentWeek} />
          )}
          {screen === 'weeks' && (
            <WeeksScreen nav={nav} ctx={ctx} currentWeek={currentWeek} />
          )}
          {screen === 'detail' && ctx.mode === 'weekly' && (
            <WeeklyDetail
              nav={nav}
              ctx={ctx}
              api={api}
              locations={locations}
              isAdmin={isAdmin}
            />
          )}
          {screen === 'detail' && ctx.mode === 'atm' && (
            <ATMDetail nav={nav} ctx={ctx} api={api} locations={locations} />
          )}
          {screen === 'settings' && (
            <SettingsMain nav={nav} locations={locations} />
          )}
          {screen === 'sLocs' && (
            <LocsList
              nav={nav}
              locations={locations}
              setLocations={setLocations}
              api={api}
            />
          )}
          {screen === 'sLocDetail' && (
            <LocDetail
              nav={nav}
              locationId={ctx.loc}
              locations={locations}
              setLocations={setLocations}
              api={api}
            />
          )}
          {screen === 'sPartners' && (
            <PartnersList nav={nav} locations={locations} />
          )}
        </div>
      </div>
    </div>
  );
}

export default withShipment(
  {
    mapStateToProps: (state) => ({ userInfo: userInfoSelector(state) }),
    actionCreators: { apiRequest },
  },
  ProfitTrackerBase,
);
